import React, { useEffect, useState } from "react";
import { Edit3, Loader2, Plus, RefreshCw, Trash2, X } from "lucide-react";
import useBulkPackageGroupSets from "../../services/Order/useBulkPackageGroupSets";
import useBulkPackages from "../../services/Order/useBulkPackages";

const field =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

const EMPTY_SET = {
  package_group_set_id: "",
  package_id: "",
  set_name: "",
  minimum_groups_to_select: "",
  maximum_groups_to_select: "",
  display_order: "",
};

const EMPTY_MEMBER = {
  package_group_set_id: "",
  package_option_group_id: "",
  new_package_group_set_id: "",
  new_package_option_group_id: "",
};

export function BulkPackageGroupSets() {
  const {
    packageGroupSetsLoading,
    packageGroupSetSaving,
    packageGroupSetDeletingId,
    getPackageGroupSets,
    insertPackageGroupSet,
    updatePackageGroupSet,
    deletePackageGroupSet,
  } = useBulkPackageGroupSets();
  const { getPackages } = useBulkPackages();

  const [rows, setRows] = useState([]);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(EMPTY_SET);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const groupSetRows = await getPackageGroupSets("");
    setRows(Array.isArray(groupSetRows) ? groupSetRows : []);
  };

  const ensurePackages = async () => {
    if (packages.length) return packages;
    const rows = await getPackages("");
    const next = Array.isArray(rows) ? rows : [];
    setPackages(next);
    return next;
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = async (row) => {
    await ensurePackages();
    setForm({
      package_group_set_id: String(row?.package_group_set_id ?? ""),
      package_id: String(row?.package_id ?? ""),
      set_name: String(row?.set_name ?? ""),
      minimum_groups_to_select: String(row?.minimum_groups_to_select ?? ""),
      maximum_groups_to_select: String(row?.maximum_groups_to_select ?? ""),
      display_order: String(row?.display_order ?? ""),
    });
    setModal(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    const saved = form.package_group_set_id
      ? await updatePackageGroupSet(form)
      : await insertPackageGroupSet(form);

    if (saved) {
      setModal(false);
      setForm(EMPTY_SET);
      load();
    }
  };

  const packageName = (id) =>
    packages.find((row) => String(row?.package_id) === String(id))
      ?.package_name || `Package #${id || "—"}`;

  return (
    <section className="space-y-3">
      <Header
        title="Package Group Sets"
        subtitle="Configure how many package option groups can be selected."
        loading={packageGroupSetsLoading}
        onRefresh={load}
        onAdd={() => {
          setForm(EMPTY_SET);
          setModal(true);
        }}
        add="Add Group Set"
      />

      <Rows
        loading={packageGroupSetsLoading}
        empty="No package group sets found."
        rows={rows}
        render={(row, index) => {
          const id = String(row?.package_group_set_id ?? "");
          return (
            <div
              key={id || index}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <b className="text-sm text-slate-900">
                  {row?.set_name || "Unnamed Group Set"}
                </b>
                <p className="text-[11px] text-slate-500">
                  ID #{id || "—"} · {packageName(row?.package_id)} · Min{" "}
                  {row?.minimum_groups_to_select || "—"} · Max{" "}
                  {row?.maximum_groups_to_select || "—"} · Order{" "}
                  {row?.display_order || "—"}
                </p>
              </div>
              <Icon onClick={() => edit(row)}>
                <Edit3 size={13} />
              </Icon>
              <Icon
                danger
                disabled={packageGroupSetDeletingId === id}
                onClick={async () => {
                  if (await deletePackageGroupSet(id)) load();
                }}
              >
                {packageGroupSetDeletingId === id ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
              </Icon>
            </div>
          );
        }}
      />

      {modal && (
        <Modal
          title={
            form.package_group_set_id
              ? "Edit Package Group Set"
              : "Add Package Group Set"
          }
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <Select
                label="Package"
                value={form.package_id}
                set={(value) =>
                  setForm((prev) => ({ ...prev, package_id: value }))
                }
              >
                <option value="">Select Package</option>
                {packages.map((row) => (
                  <option key={row?.package_id} value={row?.package_id}>
                    {row?.package_name || `Package #${row?.package_id}`}
                  </option>
                ))}
              </Select>
              <Input
                label="Set Name"
                name="set_name"
                form={form}
                setForm={setForm}
              />
              <Input
                label="Minimum Groups To Select"
                name="minimum_groups_to_select"
                form={form}
                setForm={setForm}
                type="number"
              />
              <Input
                label="Maximum Groups To Select"
                name="maximum_groups_to_select"
                form={form}
                setForm={setForm}
                type="number"
              />
              <Input
                label="Display Order"
                name="display_order"
                form={form}
                setForm={setForm}
                type="number"
              />
            </div>
            <Footer
              saving={packageGroupSetSaving}
              edit={!!form.package_group_set_id}
              close={() => setModal(false)}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

export function BulkPackageGroupSetMembers() {
  const {
    packageGroupSetsLoading,
    packageGroupSetMembersLoading,
    packageGroupSetMemberSaving,
    packageGroupSetMemberDeletingKey,
    getPackageGroupSets,
    getPackageGroupSetMembers,
    insertPackageGroupSetMember,
    updatePackageGroupSetMember,
    deletePackageGroupSetMember,
  } = useBulkPackageGroupSets();
  const { getPackageOptionGroups } = useBulkPackages();

  const [sets, setSets] = useState([]);
  const [optionGroups, setOptionGroups] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [editing, setEditing] = useState(false);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const memberRows = await getPackageGroupSetMembers("", "");
    setRows(Array.isArray(memberRows) ? memberRows : []);
  };

  const ensureMemberOptions = async () => {
    const [groupSetRowsRaw, optionGroupRowsRaw] = await Promise.all([
      sets.length ? Promise.resolve(sets) : getPackageGroupSets(""),
      optionGroups.length
        ? Promise.resolve(optionGroups)
        : getPackageOptionGroups(""),
    ]);
    setSets(Array.isArray(groupSetRowsRaw) ? groupSetRowsRaw : []);
    setOptionGroups(
      Array.isArray(optionGroupRowsRaw) ? optionGroupRowsRaw : [],
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const edit = async (row) => {
    await ensureMemberOptions();
    const groupSetId = String(row?.package_group_set_id ?? "");
    const optionGroupId = String(row?.package_option_group_id ?? "");
    setForm({
      package_group_set_id: groupSetId,
      package_option_group_id: optionGroupId,
      new_package_group_set_id: groupSetId,
      new_package_option_group_id: optionGroupId,
    });
    setEditing(true);
    setModal(true);
  };

  const submit = async (event) => {
    event.preventDefault();
    const saved = editing
      ? await updatePackageGroupSetMember(form)
      : await insertPackageGroupSetMember(form);

    if (saved) {
      setModal(false);
      setEditing(false);
      setForm(EMPTY_MEMBER);
      load();
    }
  };

  const setName = (id) =>
    sets.find((row) => String(row?.package_group_set_id) === String(id))
      ?.set_name || `Group Set #${id || "—"}`;

  const optionGroupName = (id) => {
    const row = optionGroups.find(
      (item) => String(item?.package_option_group_id) === String(id),
    );
    return row?.group_title || `Package Option Group #${id || "—"}`;
  };

  return (
    <section className="space-y-3">
      <Header
        title="Package Group Set Members"
        subtitle="Map package option groups into package group sets."
        loading={packageGroupSetMembersLoading || packageGroupSetsLoading}
        onRefresh={load}
        onAdd={() => {
          setForm(EMPTY_MEMBER);
          setEditing(false);
          setModal(true);
        }}
        add="Add Member"
      />

      <Rows
        loading={packageGroupSetMembersLoading}
        empty="No package group set members found."
        rows={rows}
        render={(row, index) => {
          const groupSetId = String(row?.package_group_set_id ?? "");
          const optionGroupId = String(row?.package_option_group_id ?? "");
          const key = `${groupSetId}:${optionGroupId}`;
          return (
            <div
              key={key || index}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <b className="text-sm text-slate-900">{setName(groupSetId)}</b>
                <p className="text-[11px] text-slate-500">
                  {optionGroupName(optionGroupId)} · Set #{groupSetId || "—"} ·
                  Option Group #{optionGroupId || "—"}
                </p>
              </div>
              <Icon onClick={() => edit(row)}>
                <Edit3 size={13} />
              </Icon>
              <Icon
                danger
                disabled={packageGroupSetMemberDeletingKey === key}
                onClick={async () => {
                  if (
                    await deletePackageGroupSetMember(groupSetId, optionGroupId)
                  ) {
                    load();
                  }
                }}
              >
                {packageGroupSetMemberDeletingKey === key ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
              </Icon>
            </div>
          );
        }}
      />

      {modal && (
        <Modal
          title={
            editing
              ? "Edit Package Group Set Member"
              : "Add Package Group Set Member"
          }
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <Select
                label={editing ? "Current Group Set" : "Group Set"}
                value={form.package_group_set_id}
                set={(value) =>
                  setForm((prev) => ({ ...prev, package_group_set_id: value }))
                }
                disabled={editing}
              >
                <option value="">Select Group Set</option>
                {sets.map((row) => (
                  <option
                    key={row?.package_group_set_id}
                    value={row?.package_group_set_id}
                  >
                    {row?.set_name || `Group Set #${row?.package_group_set_id}`}
                  </option>
                ))}
              </Select>

              <Select
                label={
                  editing
                    ? "Current Package Option Group"
                    : "Package Option Group"
                }
                value={form.package_option_group_id}
                set={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    package_option_group_id: value,
                  }))
                }
                disabled={editing}
              >
                <option value="">Select Package Option Group</option>
                {optionGroups.map((row) => (
                  <option
                    key={row?.package_option_group_id}
                    value={row?.package_option_group_id}
                  >
                    {row?.group_title ||
                      `Package Option Group #${row?.package_option_group_id}`}
                  </option>
                ))}
              </Select>

              {editing && (
                <>
                  <Select
                    label="New Group Set"
                    value={form.new_package_group_set_id}
                    set={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        new_package_group_set_id: value,
                      }))
                    }
                  >
                    <option value="">Select New Group Set</option>
                    {sets.map((row) => (
                      <option
                        key={row?.package_group_set_id}
                        value={row?.package_group_set_id}
                      >
                        {row?.set_name ||
                          `Group Set #${row?.package_group_set_id}`}
                      </option>
                    ))}
                  </Select>

                  <Select
                    label="New Package Option Group"
                    value={form.new_package_option_group_id}
                    set={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        new_package_option_group_id: value,
                      }))
                    }
                  >
                    <option value="">Select New Package Option Group</option>
                    {optionGroups.map((row) => (
                      <option
                        key={row?.package_option_group_id}
                        value={row?.package_option_group_id}
                      >
                        {row?.group_title ||
                          `Package Option Group #${row?.package_option_group_id}`}
                      </option>
                    ))}
                  </Select>
                </>
              )}
            </div>
            <Footer
              saving={packageGroupSetMemberSaving}
              edit={editing}
              close={() => setModal(false)}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

function Header({ title, subtitle, loading, onRefresh, onAdd, add }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="text-[11px] text-slate-500">{subtitle}</p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex h-8 items-center gap-1 rounded-lg border px-3 text-xs"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
        >
          <Plus size={13} />
          {add}
        </button>
      </div>
    </div>
  );
}

function Rows({ loading, empty, rows, render }) {
  if (loading)
    return <div className="h-24 animate-pulse rounded-xl border bg-white" />;
  if (!rows?.length)
    return (
      <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
        {empty}
      </div>
    );
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {rows.map(render)}
    </div>
  );
}

function Modal({ title, close, children }) {
  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">{title}</h3>
          <button type="button" onClick={close}>
            <X size={17} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Select({ label, value, set, children, disabled = false }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => set(event.target.value)}
        disabled={disabled}
        className={field}
      >
        {children}
      </select>
    </label>
  );
}

function Input({ label, name, form, setForm, type = "text" }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <input
        type={type}
        value={form[name]}
        onChange={(event) =>
          setForm((prev) => ({ ...prev, [name]: event.target.value }))
        }
        className={field}
      />
    </label>
  );
}

function Icon({ children, danger, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border ${
        danger
          ? "border-rose-100 bg-rose-50 text-rose-600"
          : "border-slate-200 text-slate-500"
      }`}
    >
      {children}
    </button>
  );
}

function Footer({ saving, edit, close }) {
  return (
    <div className="flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
      <button
        type="button"
        onClick={close}
        disabled={saving}
        className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={saving}
        className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
      >
        {saving ? (
          <Loader2 size={13} className="animate-spin" />
        ) : edit ? (
          <Edit3 size={13} />
        ) : (
          <Plus size={13} />
        )}
        {saving ? "Saving..." : edit ? "Update" : "Add"}
      </button>
    </div>
  );
}
